import { DateTime } from 'luxon';

import { DASH } from '@/globals';

import Plan from './plan/Plan';
import PublicPlanSections from './plan/types/PublicPlanSections';
import { ISharedPlanData, SharedPlanData } from './plan/types/SharedPlanData';

interface ISharedPublicEntitySettings {
  settings: Record<PublicPlanSections, boolean>;
  publicPlaintextPassword: string;
  active: boolean;
  jsonDataString: ISharedPlanData;
  expiryDate?: string;
  modifiedOnUtc?: string;
  createdOnUtc?: string;
}

export interface ISharedPublicCampaignEntityToAPI {
  public_plaintext_password: string;
  settings: Record<string, boolean>;
  active: boolean;
}

interface ISharedPublicPlanEntitySettings extends ISharedPublicCampaignEntityToAPI {
  settings: Record<PublicPlanSections, boolean>;
  json_data_string: string;
}

export type TSharedPublicEntityAPI =
  | { shared_plan: ISharedPublicPlanEntitySettings }
  | ISharedPublicCampaignEntityToAPI;

class SharedPublicEntity {
  constructor(
    public publicPlaintextPassword: string,
    public settings: Record<PublicPlanSections, boolean>,
    public active: boolean,
    public jsonDataString?: ISharedPlanData,
    public plan?: Plan,
    public expiryDate?,
    public modifiedOnUtc?,
    public createdOnUtc?,
  ) {}

  public get id() {
    return this.plan?.id ?? -1;
  }

  public get isPlan() {
    return this.plan ? true : false;
  }

  public get expiryDateFormatted() {
    return DateTime.fromISO(this.expiryDate).toLocal().toLocaleString(DateTime.DATETIME_MED) ?? DASH;
  }

  public get isExpiringInLessThan7Days() {
    const currentDate = DateTime.local();
    const expirationDate = DateTime.fromISO(this.expiryDate);
    const daysUntilExpiration = Math.ceil(expirationDate.diff(currentDate, 'days').toObject().days);
    if (daysUntilExpiration > 0 && daysUntilExpiration <= 7) {
      return daysUntilExpiration;
    }
    return 0;
  }

  public get isExpired() {
    const today = DateTime.local();
    const expiry = DateTime.fromISO(this.expiryDate);
    return today > expiry;
  }

  static fromObject(sharingSettings: Partial<ISharedPublicEntitySettings> & { plan: Plan }) {
    return new SharedPublicEntity(
      sharingSettings.publicPlaintextPassword,
      sharingSettings.settings,
      sharingSettings.active,
      sharingSettings.jsonDataString,
      sharingSettings.plan,
    );
  }

  public toApi(): TSharedPublicEntityAPI {
    if (this.isPlan) {
      return {
        shared_plan: {
          public_plaintext_password: this.publicPlaintextPassword,
          settings: this.settings,
          json_data_string: SharedPlanData.serialize(this.jsonDataString),
          active: this.active,
        },
      };
    }
    return {
      public_plaintext_password: this.publicPlaintextPassword,
      settings: this.settings,
      active: this.active,
    };
  }

  static fromSharedPublicEntityApi(apiObject) {
    return new SharedPublicEntity(
      apiObject.public_plaintext_password,
      apiObject.settings,
      apiObject.active,
      apiObject.json_data_string,
      null,
      apiObject.expiry_date,
      apiObject.modified_on_utc,
      apiObject.created_on_utc,
    );
  }

  static fromApi(apiObject): SharedPublicEntity {
    return SharedPublicEntity.fromObject({
      plan: Plan.fromApi(apiObject),
    });
  }
}

export default SharedPublicEntity;
